import { useSyncExternalStore } from "react";

// PORT OF `AiAvailabilityService` FOR THE "communications" CHANNEL: AN AI CALL THAT FAILS IS
// REPORTED AT ONCE, THEN A PROBE DECIDES WHETHER AI IS ACTUALLY DOWN. IF IT IS, THE CHANNEL
// GOES UNAVAILABLE (THE GENERATE BUTTONS DISABLE) AND THE PROBE IS POLLED WITH BACKOFF - 3S,
// DOUBLING TO A 10S CEILING - UNTIL IT ANSWERS, OR UNTIL 20 FAILED PROBES, WHEN THE CHANNEL
// IS RE-ENABLED SO THE NEXT USER ACTION RE-ARMS THE CYCLE INSTEAD OF STAYING STUCK. ONE STATE
// FOR THE WHOLE APP, AS THE ROOT SERVICE IS.

export type GuardedOutcome<T> = { status: "success"; value: T } | { status: "error"; error: unknown } | { status: "unavailable"; error: unknown } | { status: "cancelled" };

export const POLL = { initialMs: 3000, maxMs: 10_000, maxAttempts: 20 };

export class AiChannel {
  private available = true;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private polling = false;
  private attempts = 0;

  get isAvailable() {
    return this.available;
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  // RUN `call`; ON FAILURE REPORT `error` AT ONCE (VIA `onError`, SO FEEDBACK IS NOT HELD BEHIND
  // THE PROBE), THEN PROBE. RESOLVES TO THE TERMINAL OUTCOME; AN ABORTED `signal` IS `cancelled`.
  async guardedCall<T>(call: () => Promise<T>, probe: () => Promise<unknown>, signal: AbortSignal, onError: (error: unknown) => void): Promise<GuardedOutcome<T>> {
    let value: T;
    try {
      value = await call();
    } catch (error) {
      if (signal.aborted) return { status: "cancelled" };
      onError(error);
      try {
        await probe();
        return { status: "error", error };
      } catch {
        if (signal.aborted) return { status: "error", error };
        this.reportFailure(probe);
        return { status: "unavailable", error };
      }
    }
    if (signal.aborted) return { status: "cancelled" };
    this.reportSuccess();
    return { status: "success", value };
  }

  reportSuccess() {
    this.stopPolling();
    this.setAvailable(true);
  }

  reportFailure(probe: () => Promise<unknown>) {
    // ALREADY DOWN AND POLLING: KEEP THE BACKOFF AND ATTEMPT COUNT WHERE THEY ARE
    if (!this.available && this.polling) return;
    this.setAvailable(false);
    this.attempts = 0;
    this.schedule(probe, POLL.initialMs);
  }

  // A DELIBERATE, HUMAN-SCALE RETRY DELAY (THE BACKOFF), NOT SEQUENCING
  private schedule(probe: () => Promise<unknown>, delay: number) {
    this.clearTimer();
    this.polling = true;
    this.timer = setTimeout(() => {
      this.timer = null;
      probe().then(
        () => this.reportSuccess(),
        () => {
          if (!this.polling) return;
          this.attempts += 1;
          if (this.attempts >= POLL.maxAttempts) {
            this.reportSuccess();
            return;
          }
          this.schedule(probe, Math.min(delay * 2, POLL.maxMs));
        },
      );
    }, delay);
  }

  private stopPolling() {
    this.clearTimer();
    this.polling = false;
    this.attempts = 0;
  }

  private clearTimer() {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private setAvailable(v: boolean) {
    if (this.available === v) return;
    this.available = v;
    this.listeners.forEach((fn) => fn());
  }
}

export const communicationsAi = new AiChannel();

export function useAiAvailable(): boolean {
  return useSyncExternalStore(communicationsAi.subscribe, () => communicationsAi.isAvailable);
}
